import { Plugin, PluginKey, type EditorState } from "@tiptap/pm/state"
import { MENTION_CATEGORY_CLASS, PromptMention } from "@/components/prompt-editor/mention-node"
import { mentionDisplayLabel, type MentionCategory } from "@/components/prompt-editor/types"
import { TASK_MENTION_START_RE, parseTaskMentionToken, taskMentionToken } from "@/lib/task-mentions"

const TASK_MENTION_HTML_TYPE = "task-mention"
const LABELED_CATEGORIES = new Set<string>(["file", "person", "task", "hero", "loc", "prop", "shot", "script"])

export type TaskMentionLabelLookup = (
  category: MentionCategory,
  id: number,
  stateId?: number | null,
) => string | undefined

function nodeStateId(attrs: { stateId?: unknown } | null | undefined): number | null {
  const raw = attrs?.stateId
  return raw == null || raw === "" ? null : Number(raw)
}

// Подпись без изменений (undefined) не трогаем; у каждого состояния своя подпись, иначе чип не перерисуется.
export function taskMentionLabelsTransaction(state: EditorState, lookup: TaskMentionLabelLookup) {
  const tr = state.tr
  state.doc.descendants((node, pos) => {
    if (node.type.name !== "mention" || !LABELED_CATEGORIES.has(node.attrs.category)) return
    const label = lookup(node.attrs.category as MentionCategory, Number(node.attrs.id), nodeStateId(node.attrs))
    if (label !== undefined && node.attrs.label !== label) tr.setNodeAttribute(pos, "label", label)
  })
  return tr.docChanged ? tr.setMeta("addToHistory", false) : null
}

export const TaskMention = PromptMention.extend({
  // Свой data-type: промпт шота разбирает span[data-type="mention"] и превратил бы вставленный чип в «файл удалён».
  parseHTML() {
    return [{ tag: `span[data-type="${TASK_MENTION_HTML_TYPE}"]` }, { tag: 'span[data-type="task-file"]' }]
  },

  renderHTML({ node, HTMLAttributes }) {
    const category = (node.attrs.category ?? "reference") as MentionCategory
    const display = mentionDisplayLabel(category, String(node.attrs.label ?? ""))
    return [
      "span",
      {
        ...HTMLAttributes,
        "data-type": TASK_MENTION_HTML_TYPE,
        class: MENTION_CATEGORY_CLASS[category] ?? "pe-mention",
        "data-category": category,
      },
      display,
    ]
  },

  renderText({ node }) {
    return taskMentionToken(String(node.attrs.category), Number(node.attrs.id), nodeStateId(node.attrs))
  },

  addProseMirrorPlugins() {
    const getLookup = () => this.options.getMentionLabel?.()
    return [
      ...(this.parent?.() ?? []),
      new Plugin({
        key: new PluginKey("taskMentionLabels"),
        appendTransaction: (transactions, _oldState, newState) => {
          const lookup = getLookup()
          if (!lookup || !transactions.some((tr) => tr.docChanged)) return null
          return taskMentionLabelsTransaction(newState, lookup)
        },
      }),
    ]
  },

  markdownTokenName: "taskMention",
  markdownTokenizer: {
    name: "taskMention",
    level: "inline",
    start: (src) => src.search(TASK_MENTION_START_RE),
    tokenize: (src) => {
      const token = parseTaskMentionToken(src)
      return token ? { type: "taskMention", ...token } : undefined
    },
  },
  parseMarkdown: (token, h) =>
    h.createNode("mention", {
      category: token.category,
      id: Number(token.id),
      label: "",
      ...(token.stateId == null ? {} : { stateId: Number(token.stateId) }),
    }),
  renderMarkdown: (node) =>
    taskMentionToken(String(node.attrs?.category), Number(node.attrs?.id), nodeStateId(node.attrs)),
})
